import { CharComp } from '@/components/CharComp'
import { CollectCoin } from '@/components/CollectCoin'
import '@/layouts/kline-panel-layout.scss'
import classNames from 'classnames'
import { useI18n } from 'i18n-utils'
import { useBaseInfoStore, useDoubleContractKlineStore, useQuoteStore, useStaticTableDataStore, useUserInfoStore } from 'store'
import { ArrowDownSolidIcon } from 'ui-components'
import ContractMiniQuotesTrigger from './ContractMiniQuotesTrigger.tsx'

interface KlinePanelProps {}

export const KlinePanel = defineComponent<KlinePanelProps>({
  name: 'KlinePanel',
  setup() {
    const quoteStore = useQuoteStore()
    const klineStore = useDoubleContractKlineStore()
    const { t } = useI18n()
    const staticTableDataStore = useStaticTableDataStore()
    const userInfoStore = useUserInfoStore()
    const baseInfoStore = useBaseInfoStore()

    const coinName = computed(() => {
      if (!klineStore.currentSymbolId) return ''
      return quoteStore.getContractSymbolName(klineStore.currentSymbolId)
    })

    const currentKline = computed(() => klineStore.currentKline)

    const currentQuote = computed(() => {
      if (!klineStore.currentSymbolId) return null
      return quoteStore.allContractList.find((item) => item.symbolId === klineStore.currentSymbolId)
    })

    const currencyList = computed(() => staticTableDataStore.currencyList)

    const mark = computed(() => currencyList.value.find((item) => item.logicName === userInfoStore.currentToCurrency)?.mark || '￥')

    const coinIcon = computed(() => {
      const url = baseInfoStore.getContractCoinIconUrlBySymbolId(klineStore.currentSymbolId!)
      if (!url) return ''
      return url
    })

    return () => (
      <div class="kline-panel-layout">
        <div class="coin-info">
          <div class="collect-coin trade-kine-icon">{klineStore.currentSymbolId && <CollectCoin symbolId={klineStore.currentSymbolId} />}</div>
          <ContractMiniQuotesTrigger>
            <div class="flex items-center coin-name">
              <img class={'coin-icon'} src={coinIcon.value} alt="" />
              <CharComp>{coinName.value}</CharComp>
              <ArrowDownSolidIcon class="ml-7" />
            </div>
          </ContractMiniQuotesTrigger>
          <div class="current-price">
            <div
              class={classNames('usdt', {
                up: klineStore.klineDirection === 'up',
                down: klineStore.klineDirection === 'down',
              })}
            >
              <CharComp>{currentKline.value?.close.formatNumberWithCommas().decimalPadZero(klineStore.coinToPrecision) || '--'}</CharComp>
            </div>

            <div class={'cny'}>
              <CharComp>
                ≈{mark.value}
                &nbsp;
                {quoteStore.getContractLastCurrencyPrice(klineStore.currentSymbolId!)}
              </CharComp>
            </div>
          </div>
        </div>
        <div class="price-change">
          <div class="price-change-item">
            <CharComp class={'label'}>{t('涨幅')}</CharComp>
            <CharComp
              class={classNames('value', {
                up: quoteStore.getContractLastPriceChange(klineStore.currentSymbolId!).isGreaterThan(0),
                down: quoteStore.getContractLastPriceChange(klineStore.currentSymbolId!).isLessThan(0),
              })}
            >
              {quoteStore.getContractLastPriceChange(klineStore.currentSymbolId!).isGreaterThan(0) ? '+' : ''}
              {quoteStore.getContractLastPriceChange(klineStore.currentSymbolId!).formatNumberWithCommas()}%
            </CharComp>
          </div>
          <div class="price-change-item">
            <CharComp class={'label'}>{t('24H高')}</CharComp>
            <CharComp class={'value'}>{currentQuote.value?.high.formatNumberWithCommas().decimalPadZero(klineStore.coinToPrecision)}</CharComp>
          </div>
          <div class="price-change-item">
            <CharComp class={'label'}>{t('24H低')}</CharComp>
            <CharComp class={'value'}>{currentQuote.value?.low.formatNumberWithCommas().decimalPadZero(klineStore.coinToPrecision)}</CharComp>
          </div>
          <div class="price-change-item">
            <CharComp class={'label'}>{t('24H量')}</CharComp>
            <CharComp class={'value'}>{currentQuote.value?.vol?.formatNumberWithCommas().decimalPadZero(klineStore.coinFromPrecision).removeTrailingZero}</CharComp>
          </div>
        </div>
      </div>
    )
  },
})
